/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

'use client';

import 'client-only';
import { Pressable } from 'react-aria/Pressable';
import {
  Children,
  type DOMAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import { DialogTrigger } from 'react-aria-components/Dialog';
import type { FocusableElement } from '@react-types/shared';
import type { PopoverTriggerProps } from './types';

/**
 * PopoverTrigger - Root component that manages popover state and positioning
 *
 * Wraps a trigger element and its associated Popover. The first child becomes
 * the trigger, and the second child should be a Popover component.
 *
 * @example
 * ```tsx
 * <PopoverTrigger>
 *   <Button>Open</Button>
 *   <Popover>
 *     <PopoverContent>Content here</PopoverContent>
 *   </Popover>
 * </PopoverTrigger>
 * ```
 *
 * @param props - {@link PopoverTriggerProps}
 * @param props.ref - Forwarded ref to the trigger element.
 * @param props.children - Trigger element and Popover component.
 * @returns The rendered PopoverTrigger component.
 */
export function PopoverTrigger({
  ref,
  children,
  ...rest
}: PopoverTriggerProps) {
  const [trigger, popover] = Children.toArray(children) as [
    ReactElement<DOMAttributes<FocusableElement>, string>,
    ReactNode,
  ];

  return (
    <DialogTrigger {...rest}>
      <Pressable ref={ref}>{trigger}</Pressable>
      {popover}
    </DialogTrigger>
  );
}
